import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface FooterConfig {
  id: string;
  secao: string;
  titulo: string;
  links: any;
  ordem: number;
  ativo: boolean;
}

const Footer = () => {
  const [footerConfigs, setFooterConfigs] = useState<FooterConfig[]>([]);

  useEffect(() => {
    carregarFooterConfig();
  }, []);

  const carregarFooterConfig = async () => {
    const { data, error } = await supabase
      .from('footer_config')
      .select('*')
      .eq('ativo', true)
      .order('ordem');

    if (!error && data) {
      setFooterConfigs(data);
    }
  };

  const currentYear = new Date().getFullYear();

  // Função para renderizar links baseada na estrutura real
  const renderLinks = (links: any) => {
    if (!links) return null;

    // Se tem propriedade 'items' (estrutura dos links_rapidos e produtos)
    if (links.items && Array.isArray(links.items)) {
      return links.items.map((item: any, index: number) => (
        <li key={index}>
          <Link
            to={item.url || '#'}
            className="text-black/80 text-sm hover:text-black transition-colors duration-300 cursor-pointer"
          >
            {item.texto || item.nome || item.title}
          </Link>
        </li>
      ));
    }

    // Se é um array direto
    if (Array.isArray(links)) {
      return links.map((link: any, index: number) => (
        <li key={index}>
          <Link
            to={link.url || '#'}
            className="text-black/80 text-sm hover:text-black transition-colors duration-300 cursor-pointer"
          >
            {link.texto || link.nome || link.title}
          </Link>
        </li>
      ));
    }

    return null;
  };

  // Função para renderizar informações de contacto
  const renderContacto = (links: any) => {
    if (!links || typeof links !== 'object') return null;

    return (
      <div className="space-y-3">
        {links.email && (
          <div className="flex items-center gap-3">
            <i className="ri-mail-line text-black"></i>
            <a 
              href={`mailto:${links.email}`}
              className="text-black/80 text-sm hover:text-black transition-colors cursor-pointer"
            >
              {links.email}
            </a>
          </div>
        )}
        {links.telefone && (
          <div className="flex items-center gap-3">
            <i className="ri-phone-line text-black"></i>
            <a 
              href={`tel:${links.telefone}`}
              className="text-black/80 text-sm hover:text-black transition-colors cursor-pointer"
            >
              {links.telefone}
            </a>
          </div>
        )}
        {links.horario && (
          <div className="flex items-center gap-3">
            <i className="ri-time-line text-black"></i>
            <span className="text-black/80 text-sm">{links.horario}</span>
          </div>
        )}
      </div>
    );
  };

  // Função para renderizar texto/descrição
  const renderTexto = (conteudo: any) => {
    if (!conteudo) return null;
    
    if (typeof conteudo === 'string') {
      return <p className="text-black/80 text-sm leading-relaxed">{conteudo}</p>;
    }
    if (conteudo.descricao) {
      return <p className="text-black/80 text-sm leading-relaxed">{conteudo.descricao}</p>;
    }
    if (conteudo.texto) {
      return <p className="text-black/80 text-sm leading-relaxed">{conteudo.texto}</p>;
    }
    return null;
  };

  // Encontrar configurações específicas
  const brandConfig = footerConfigs.find(config => config.secao === 'brand');
  const outrasConfigs = footerConfigs.filter(config => config.secao !== 'brand' && config.secao !== 'redes_sociais');

  return (
    <footer className="bg-gradient-to-br from-luxury-gold via-luxury-silver to-luxury-gold py-16 px-8 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Logo/Brand - Primeira coluna */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-gold rounded-lg flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/73405d7344672589d1b3b209adb44c46/fd92e79b39ca1247f5209e7dcfe8b77c.png" 
                  alt="Dindimcast Logo" 
                  className="w-full h-full object-contain p-1"
                />
              </div>
              <span className="text-white text-2xl font-serif font-bold">
                Dindimcast
              </span>
            </div>
            {brandConfig ? (
              renderTexto(brandConfig.links)
            ) : (
              <p className="text-black/80 text-sm leading-relaxed">
                Transformando vidas através da educação digital de qualidade.
              </p>
            )}
          </div>

          {/* Renderizar outras seções */}
          {outrasConfigs.slice(0, 3).map((config) => (
            <div key={config.id}>
              <h3 className="text-black text-lg font-bold mb-6">{config.titulo}</h3>
              
              {config.secao === 'contacto' ? (
                renderContacto(config.links)
              ) : (
                <ul className="space-y-3">
                  {renderLinks(config.links)}
                </ul>
              )}
            </div>
          ))}

          {/* Se não houver configurações suficientes, usar conteúdo padrão */}
          {outrasConfigs.length === 0 && (
            <>
              <div>
                <h3 className="text-black text-lg font-bold mb-6">Links Rápidos</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/" className="text-black/80 text-sm hover:text-black transition-colors duration-300 cursor-pointer">
                      Início
                    </Link>
                  </li>
                  <li>
                    <Link to="/produtos" className="text-black/80 text-sm hover:text-black transition-colors duration-300 cursor-pointer">
                      Produtos
                    </Link>
                  </li>
                  <li>
                    <Link to="/sobre" className="text-black/80 text-sm hover:text-black transition-colors duration-300 cursor-pointer">
                      Sobre
                    </Link>
                  </li>
                  <li>
                    <Link to="/equipa" className="text-black/80 text-sm hover:text-black transition-colors duration-300 cursor-pointer">
                      Equipa
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-black text-lg font-bold mb-6">Recursos</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/area-cliente" className="text-black/80 text-sm hover:text-black transition-colors duration-300 cursor-pointer">
                      Área Cliente
                    </Link>
                  </li>
                  <li>
                    <Link to="/contactos" className="text-black/80 text-sm hover:text-black transition-colors duration-300 cursor-pointer">
                      Contactos
                    </Link>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Copyright */}
        <div className="border-t border-black/20 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-black/80 text-sm text-center md:text-left">
              © {currentYear} Dindimcast. Todos os direitos reservados.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-black/80 text-sm hover:text-black transition-colors duration-300 cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
